import client from 'helpers/client';
import cognito from 'helpers/cognito';

const getAuthHeaders = async () => {
  const auth = await cognito.getHeaderAuthorization();
  return { headers: { ...auth } };
};

const withWalletQuery = (filters = {}, walletAddress) => {
  const next = { ...filters };
  if (walletAddress) {
    next.walletAddress = String(walletAddress).trim();
  }
  return next;
};

const withWallet = (body = {}, walletAddress) => {
  if (!walletAddress) return body || {};
  return { ...(body || {}), walletAddress: String(walletAddress).trim() };
};

const buildQuery = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, value);
    }
  });
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const getPublicMicrojobPayoutLedger = async (daoId) => {
  const response = await client.api(
    `/microjob/payouts/transparency?daoId=${encodeURIComponent(daoId)}`,
    'get',
  );
  return response.data;
};

export const listMicrojobCycles = async (daoId, filters = {}, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/microjob/cycles${buildQuery(withWalletQuery(filters, walletAddress))}`,
    'get',
    await getAuthHeaders(),
  );
  return response.data;
};

export const getMicrojobCycle = async (daoId, cycleId, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/microjob/cycles/${encodeURIComponent(cycleId)}${buildQuery(withWalletQuery({}, walletAddress))}`,
    'get',
    await getAuthHeaders(),
  );
  return response.data;
};

export const createMicrojobCycle = async (daoId, body, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/microjob/cycles`,
    'post',
    withWallet(body, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const updateMicrojobCycle = async (daoId, cycleId, body, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/microjob/cycles/${encodeURIComponent(cycleId)}`,
    'patch',
    withWallet(body, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const deleteMicrojobCycle = async (daoId, cycleId, walletAddress) => {
  const auth = await getAuthHeaders();
  const response = await client.api(
    `/daos/${daoId}/microjob/cycles/${encodeURIComponent(cycleId)}`,
    'delete',
    {
      headers: auth.headers,
      data: withWallet({}, walletAddress),
    },
  );
  return response.data;
};

export const importMicrojobSessionLog = async (daoId, periodStart, periodEnd, walletAddress) => {
  const params = new URLSearchParams({ periodStart, periodEnd });
  if (walletAddress) params.append('walletAddress', String(walletAddress).trim());
  const response = await client.api(
    `/daos/${daoId}/microjob/import-session-log?${params.toString()}`,
    'get',
    await getAuthHeaders(),
  );
  return response.data;
};

export const importMicrojobCsv = async (daoId, cycleId, rows, walletAddress, options = {}) => {
  const body = { rows };
  if (options.skipMemberCheck) body.skipMemberCheck = true;
  const response = await client.api(
    `/daos/${daoId}/microjob/cycles/${encodeURIComponent(cycleId)}/import-csv`,
    'post',
    withWallet(body, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const submitMicrojobCycle = async (daoId, cycleId, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/microjob/cycles/${encodeURIComponent(cycleId)}/submit`,
    'post',
    withWallet({}, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const approveMicrojobCycle = async (daoId, cycleId, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/microjob/cycles/${encodeURIComponent(cycleId)}/approve`,
    'post',
    withWallet({}, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const rejectMicrojobCycle = async (daoId, cycleId, reviewNote, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/microjob/cycles/${encodeURIComponent(cycleId)}/reject`,
    'post',
    withWallet({ reviewNote }, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const getMicrojobExecutePayload = async (daoId, cycleId, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/microjob/cycles/${encodeURIComponent(cycleId)}/execute-payload${buildQuery(withWalletQuery({}, walletAddress))}`,
    'get',
    await getAuthHeaders(),
  );
  return response.data;
};

export const markMicrojobExecuted = async (daoId, cycleId, onChainTxHash, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/microjob/cycles/${encodeURIComponent(cycleId)}/executed`,
    'post',
    withWallet({ onChainTxHash }, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};
